import _ from 'lodash';
import Style from './reportExportModal.module.scss';
import { useModal, UseModalProps } from 'src/application/hooks/useModal';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { ReportExportingFormType, ReportExportingOptions, ReportScopeEnum } from './reportExporting';
import { useFileTypeOptions, useReportVariantOptions } from './useReportExportingOptions';
import { useTranslation } from 'react-i18next';
import { FC, MutableRefObject, PropsWithChildren, useCallback, useMemo, useRef } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { MetricActions, MetricCategories } from 'src/application/analitics/umami/TrackEventParams';
import { useUmamiTracker } from 'src/application/analitics/umami/useUmamiTracker';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { ConfirmResult } from 'src/components/dialogModal/useConfirmationModal';
import { IPatientListDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { HookFormRadioGroup } from 'src/hookFormControls/hookFormRadioGroup';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { AppSwitcherInput } from 'uikit/inputs';
import { AppInputError } from 'uikit/wrappers';
import { Loading } from 'uikit/suspense/Loading';
import { HookFormCheckboxGroupField } from 'src/hookFormControls/HookFormCheckboxGroupField';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { useGetDataForExporting } from './useGetDataForExporting';
import { useExportReportHandlers } from './reportExportHandlers';
import { logger } from 'src/application/logging/logging';
import { ReactComponent as Warning } from 'assets/img/common/tiny_warning_16.svg';

export const useReportExporting = (options: ReportExportingOptions) => {
  const reportTypeOptions = useReportVariantOptions(options.scope);
  const promiseRef = useRef<(test: ConfirmResult) => void>();
  const modal = useModal();
  const study = useStudy();

  const startExportHandler = useCallback(() => {
    modal.openModal();
    return new Promise<ConfirmResult>((resolve) => {
      promiseRef.current = resolve;
    });
  }, [modal]);

  return {
    canExport:
      reportTypeOptions.length > 0 &&
      // NOTE: In eCRF study data can be exported only for archived patients
      (options.scope === ReportScopeEnum.Study || !study?.hasEcrf || !!options.patient?.archivedBy),
    component: modal.visible ? (
      <ExportModal
        modal={modal}
        reportVariant={options.scope}
        patient={options.scope === ReportScopeEnum.Patient ? options.patient : undefined}
        promise={promiseRef}
      />
    ) : null,
    startExport: startExportHandler,
  };
};

const ExportModal: FC<{
  modal: UseModalProps;
  reportVariant: ReportScopeEnum;
  promise: MutableRefObject<((test: ConfirmResult) => void) | undefined>;
  patient?: IPatientListDto;
}> = ({ modal, reportVariant, patient, promise }) => {
  const { t } = useTranslation();
  const study = useStudy();
  const { trackEvent } = useUmamiTracker();
  const exportReport = useExportReportHandlers();
  const modalTitle = useMemo(
    () => ({
      [ReportScopeEnum.Study]: t('LogExport.StudyExportModalTitle'),
      [ReportScopeEnum.Patient]: t('LogExport.PatientExportModalTitle'),
    }),
    [t],
  );

  const exportVariantOptions = useReportVariantOptions(reportVariant);
  const fileTypes = useFileTypeOptions();

  const defaultValues = useMemo(
    () =>
      ({
        reportVariant: _.first(exportVariantOptions)?.key,
        fileType: _.first(fileTypes)?.key,
        formTypes: [],
        surveyTypes: [],
        noteTypes: [],
        includeAllVersions: false,
        withMissedSurveys: false,
        signedPdf: false,
      } satisfies ReportExportingFormType),
    [exportVariantOptions, fileTypes],
  );

  const form = useForm<ReportExportingFormType>({
    defaultValues: defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    reportVariant: selectedReportVariant,
    surveyTypes: selectedSurveyTypes,
    formTypes: selectedFormTypes,
    noteTypes: selectedNoteTypes,
  } = form.watch();

  const exportHandler = form.handleSubmit(async (data: ReportExportingFormType): Promise<void> => {
    if (!data.fileType || !data.reportVariant || !study?.id) throw new Error('Fields is required');

    try {
      await exportReport({
        scope: reportVariant,
        logType: data.reportVariant,
        patient: patient,
        fileType: data.fileType,
        includeAllVersions: data.includeAllVersions,
        signedPdf: data.signedPdf,
        withMissedSurveys: data.withMissedSurveys,
        formTypes: [...data.formTypes, ...selectedNoteTypes],
        surveyTypes: data.surveyTypes,
      });
    } catch (error: any) {
      logger().error('Export log failed', error);
      form.setError('root', { message: t('LogExport.CommonError') });
      return;
    }

    trackEvent({
      category: MetricCategories.Patient,
      action: MetricActions.ButtonPressed,
      name: 'Export logs for patient',
    });

    modal.closeModal();
    promise.current?.(ConfirmResult.Confirm);
  });

  const onCLoseHandler = useCallback(() => {
    modal.closeModal();
    promise.current?.(ConfirmResult.Cancel);
  }, [modal, promise]);

  const disableButtonReason = useMemo(
    () => [
      {
        condition: !selectedReportVariant,
        tooltip: t('LogExport.ExportButtonDisableReasons.VariantIsNotSelected'),
      },
      {
        condition:
          selectedReportVariant === 'Statistics' &&
          !selectedSurveyTypes?.length &&
          !selectedFormTypes?.length &&
          !selectedNoteTypes?.length,
        tooltip: t('LogExport.ExportButtonDisableReasons.NoData'),
      },
    ],
    [selectedFormTypes?.length, selectedNoteTypes?.length, selectedReportVariant, selectedSurveyTypes?.length, t],
  );

  return (
    <DialogModal
      title={modalTitle[reportVariant]}
      visible={modal.visible}
      onHide={onCLoseHandler}
      onDismissed={() => form.reset(defaultValues)}
      bodyClassName={Style.modalBody}
      testId={'export-logs-form'}
      footer={{
        errors: form.formState.errors.root?.message,
        leftButton: {
          text: t('Common_Cancel'),
          onClick: onCLoseHandler,
          disabled: form.formState.isSubmitting,
        },
        rightButton: {
          text: t('Common_Export'),
          onClick: exportHandler,
          isLoading: form.formState.isSubmitting,
          disabled: form.formState.isSubmitting || disableButtonReason.some((x) => x.condition),
        },
      }}
    >
      <FormProvider {...form}>
        <ExportModalContent reportVariant={reportVariant} patient={patient} />
      </FormProvider>
    </DialogModal>
  );
};

const ExportModalContent: FC<{ reportVariant: ReportScopeEnum; patient?: IPatientListDto }> = ({
  reportVariant,
  patient,
}) => {
  const { t } = useTranslation();
  const form = useFormContext<ReportExportingFormType>();
  const { hasStudyClaim } = useHasStudyClaims();
  const study = useStudy();

  const reportVariantOptions = useReportVariantOptions(reportVariant);
  const fileTypeOptions = useFileTypeOptions();

  const {
    reportVariant: selectedReportVariant,
    fileType: selectedFileType,
    withMissedSurveys: selectedWithMissedSurveys,
  } = form.watch();

  const {
    isInitialLoading,
    formTypes,
    surveyTypes: surveyType,
    noteFormTypes,
  } = useGetDataForExporting({
    patient: patient,
    fileType: selectedFileType,
    withMissedSurveys: selectedWithMissedSurveys,
    reportVariant: selectedReportVariant,
  });

  return (
    <div className={Style.content}>
      <HookFormRadioGroup
        control={form.control}
        name={'reportVariant'}
        isVertical={true}
        labelProps={{
          text: t('LogExport.ExportTypes.Header'),
        }}
        options={reportVariantOptions}
        valueField={'key'}
        labelField={'text'}
      />

      {selectedReportVariant === 'Statistics' && (
        <>
          {reportVariant === ReportScopeEnum.Patient && (
            <HookFormRadioGroup
              control={form.control}
              name={'fileType'}
              labelProps={{
                text: t('LogExport.ExportTypes.FileTypeHeader'),
              }}
              options={fileTypeOptions}
              valueField={'key'}
              labelField={'text'}
            />
          )}

          {selectedFileType === 'Pdf' && study?.hasPdfSigning && hasStudyClaim(StudyRoleClaimValue.ExportForm) && (
            <Controller
              control={form.control}
              name={'signedPdf'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <AppInputError errors={error?.message}>
                  <AppSwitcherInput
                    label={t('LogExport.SignPdf')}
                    className={Style.switcher}
                    onChange={onChange}
                    checked={value}
                  />
                </AppInputError>
              )}
            />
          )}

          <Loading
            loading={isInitialLoading}
            renderChildren={false}
            containerClassName={Style.loader}
            testId={'report-export'}
          >
            <NoDataPlaceholder showPlaceholder={!surveyType.length && !formTypes?.length && !noteFormTypes?.length}>
              <SurveySection surveyTypes={surveyType} />
              <RecordSection recordTypes={formTypes} />
              <NoteSection noteFormTypes={noteFormTypes} />
            </NoDataPlaceholder>
          </Loading>
        </>
      )}
    </div>
  );
};

const NoDataPlaceholder: FC<
  PropsWithChildren & {
    showPlaceholder: boolean;
  }
> = ({ children, showPlaceholder }) => {
  const { t } = useTranslation();

  if (showPlaceholder)
    return (
      <div className={Style.noDataPlaceholder}>
        <Warning />
        <div>{t('LogExport.NoDataPlaceholder')}</div>
      </div>
    );

  return <>{children}</>;
};

const SurveySection: FC<{ surveyTypes: string[] | undefined }> = ({ surveyTypes }) => {
  const { t } = useTranslation();
  const form = useFormContext<ReportExportingFormType>();
  const selectedFileType = form.watch('fileType');

  const surveyTypeOptions = useMemo(
    () =>
      surveyTypes?.map(
        (x) =>
          ({
            key: x,
            text: x,
          } satisfies DropdownOption<string>),
      ) ?? [],
    [surveyTypes],
  );

  if (!surveyTypeOptions?.length) return null;

  return (
    <div className={Style.sectionContainer}>
      <HookFormCheckboxGroupField
        control={form.control}
        name={'surveyTypes'}
        withSelectAllButton={surveyTypeOptions.length > 1}
        errorProps={{
          hideErrorPlaceholder: selectedFileType === 'Pdf',
        }}
        labelProps={{
          text: t('LogExport.SurveySectionHeader'),
          className: Style.sectionContainer,
          disabled: form.formState.isSubmitting,
        }}
        options={surveyTypeOptions}
        valueField={'key'}
        labelField={'text'}
        disabled={form.formState.isSubmitting}
      />

      {selectedFileType === 'Pdf' && (
        <Controller
          control={form.control}
          name={'withMissedSurveys'}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <AppInputError errors={error?.message}>
              <AppSwitcherInput
                label={t('LogExport.IncludeMissingSurveys')}
                className={Style.switcher}
                onChange={onChange}
                checked={value}
                disabled={form.formState.isSubmitting}
              />
            </AppInputError>
          )}
        />
      )}
    </div>
  );
};

const RecordSection: FC<{ recordTypes: string[] | undefined }> = ({ recordTypes }) => {
  const { t } = useTranslation();
  const form = useFormContext<ReportExportingFormType>();

  const recordTypeOptions = useMemo(
    () =>
      recordTypes?.map(
        (x) =>
          ({
            key: x,
            text: x,
          } satisfies DropdownOption<string>),
      ) ?? [],
    [recordTypes],
  );

  if (!recordTypeOptions?.length) return null;

  return (
    <div className={Style.sectionContainer}>
      <HookFormCheckboxGroupField
        control={form.control}
        name={'formTypes'}
        withSelectAllButton={recordTypeOptions.length > 1}
        errorProps={{
          hideErrorPlaceholder: true,
        }}
        labelProps={{
          text: t('LogExport.RecordSectionHeader'),
          className: Style.sectionContainer,
          disabled: form.formState.isSubmitting,
        }}
        options={recordTypeOptions}
        valueField={'key'}
        labelField={'text'}
        disabled={form.formState.isSubmitting}
      />

      <Controller
        control={form.control}
        name={'includeAllVersions'}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <AppInputError errors={error?.message}>
            <AppSwitcherInput
              label={t('LogExport.ExportAllResultVersions')}
              className={Style.switcher}
              onChange={onChange}
              checked={value}
              disabled={form.formState.isSubmitting}
            />
          </AppInputError>
        )}
      />
    </div>
  );
};

const NoteSection: FC<{ noteFormTypes: string[] | undefined }> = ({ noteFormTypes }) => {
  const { t } = useTranslation();
  const form = useFormContext<ReportExportingFormType>();

  const notesTypeOptions = useMemo(
    () =>
      noteFormTypes?.map(
        (x) =>
          ({
            key: x,
            text: x,
          } satisfies DropdownOption<string>),
      ) ?? [],
    [noteFormTypes],
  );

  if (!notesTypeOptions?.length) return null;

  return (
    <div className={Style.sectionContainer}>
      <HookFormCheckboxGroupField
        control={form.control}
        name={'formTypes'}
        withSelectAllButton={notesTypeOptions.length > 1}
        errorProps={{
          hideErrorPlaceholder: true,
        }}
        labelProps={{
          text: t('LogExport.NotesSectionHeader'),
          className: Style.sectionContainer,
          disabled: form.formState.isSubmitting,
        }}
        options={notesTypeOptions}
        valueField={'key'}
        labelField={'text'}
        disabled={form.formState.isSubmitting}
      />
    </div>
  );
};
